// src/Components/Broadcast/components/UserSelector.js
import React from 'react';
import { Users, Crown, Search, X, CheckCircle } from 'lucide-react';

export const UserSelector = ({
  showUserSelector,
  setShowUserSelector,
  searchTerm,
  setSearchTerm,
  selectedUsers,
  allUsers,  // Добавляем всех пользователей
  filteredUsers,
  toggleUserSelection,
  selectAllFiltered,
  clearSelection,
  getFilteredUsersBySearch,
  handleFilterChange,
  showNotification
}) => {
  if (!showUserSelector) return null;

  // Получаем отфильтрованных пользователей для отображения
  const displayUsers = getFilteredUsersBySearch(searchTerm);
  
  // Если нет отфильтрованных пользователей, но есть все пользователи, показываем всех
  const usersToShow = displayUsers && displayUsers.length > 0 
    ? displayUsers 
    : (allUsers && allUsers.length > 0 ? allUsers : []);

  console.log('UserSelector render:', {
    allUsersCount: allUsers?.length || 0,
    filteredUsersCount: filteredUsers?.length || 0,
    displayUsersCount: displayUsers?.length || 0,
    usersToShowCount: usersToShow.length,
    searchTerm
  });

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-gray-900 rounded-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden shadow-2xl border border-gray-700">
        <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-400" />
            Выбор получателей
          </h2>
          <button
            onClick={() => setShowUserSelector(false)}
            className="p-1 text-gray-400 hover:text-gray-300 hover:bg-gray-800 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {/* Поиск */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Поиск по имени, username, ID, телефону или email..."
                className="w-full pl-9 pr-4 py-2 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (selectAllFiltered) {
                    selectAllFiltered();
                  }
                }}
                className="px-3 py-1.5 bg-blue-900/30 text-blue-300 text-sm rounded-lg border border-blue-700 hover:bg-blue-800/30"
              >
                Выбрать всех
              </button>
              <button
                onClick={clearSelection}
                className="px-3 py-1.5 bg-red-900/30 text-red-300 text-sm rounded-lg border border-red-700 hover:bg-red-800/30"
              >
                Очистить
              </button>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-400">
                Найдено: {usersToShow.length} пользователей
              </span>
              <span className="text-sm text-blue-400 font-medium">
                Выбрано: {selectedUsers?.length || 0}
              </span>
            </div>
          </div>

          <div className="overflow-y-auto max-h-[calc(80vh-240px)] space-y-2">
            {usersToShow.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">Пользователи не найдены</p>
                {allUsers?.length === 0 ? (
                  <p className="text-sm text-gray-500 mt-1">
                    Загрузка пользователей...
                  </p>
                ) : searchTerm ? (
                  <p className="text-sm text-gray-500 mt-1">
                    Попробуйте изменить поисковый запрос
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 mt-1">
                    Нет доступных пользователей
                  </p>
                )}
              </div>
            ) : (
              usersToShow.map(user => (
                <div
                  key={user.id}
                  onClick={() => toggleUserSelection(user.id)}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedUsers?.includes(user.id)
                      ? 'bg-blue-900/30 border-blue-700'
                      : 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-900/50 to-purple-900/50 flex items-center justify-center flex-shrink-0 border border-gray-600">
                      {user.premium ? (
                        <Crown className="w-4 h-4 text-yellow-400" />
                      ) : (
                        <Users className="w-4 h-4 text-gray-400" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white truncate">
                          {user.fullName || 'Без имени'}
                        </span>
                        {user.premium && (
                          <span className="text-xs px-1.5 py-0.5 bg-yellow-900/30 text-yellow-300 rounded-full border border-yellow-700">
                            Premium
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        {user.username && <span>@{user.username}</span>}
                        {user.id && <span>ID: {user.id.substring(0, 8)}...</span>}
                        {user.phone && <span>• {user.phone}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="ml-2">
                    {selectedUsers?.includes(user.id) && (
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            onClick={() => {
              setSearchTerm('');
              setShowUserSelector(false);
            }}
            className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
          >
            Отмена
          </button>
          <button
            onClick={() => {
              handleFilterChange('selected');
              setSearchTerm('');
              setShowUserSelector(false);
              if (showNotification) {
                showNotification('success', `Выбрано ${selectedUsers?.length || 0} получателей`);
              }
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Применить ({selectedUsers?.length || 0})
          </button>
        </div>
      </div>
    </div>
  );
};